/**
 * NDT field-ticket pieces (batch 4, fieldiq-api#1157) shared by the register,
 * the ticket page, the job's Field tickets panel and the phone.
 *
 * LINE FORMS. The office (NDT_DISPATCH, `canPrice`) may add a line from the
 * rate card or at its own price, change a quantity or a price, pick the rate
 * line for an unpriced / tied line, and remove a line. A crew member adds from
 * the rate card with a quantity and changes quantities — nothing they can send
 * carries a price, because the API refuses one (403 `rate_office_only`) and a
 * control that is always refused is not offered.
 *
 * NO FORM SENDS AN AMOUNT. Each sends a quantity and, for the office, a rate;
 * the API answers the whole ticket with the line's amount and the subtotal.
 */
import React, { useMemo, useState } from 'react';
import { CalendarCheck, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAddNdtTicketLine, useCloseNdtDay, useNdtJobTickets, useNdtRates, useRebuildNdtTickets, useRemoveNdtTicketLine, useUpdateNdtTicketLine } from '../../hooks/useNdtTickets';
import { formatInstantTenantDay } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  NDT_OFFICE_LINE_UNITS,
  ndtAmountFromInput,
  ndtLineUnitLabel,
  ndtManualRateChoices,
  ndtRatePickChoices,
  ndtRateText,
  ndtSubtotalText,
  ndtTicketRefusal,
  ndtTicketScopeText,
  ndtTicketStatusSkin,
  ndtUnitBasis,
} from '../../lib/ndt-tickets';
import type { NdtJobDetail } from '../../types/ndt-inspection';
import type { NdtLineCreate, NdtLineUpdate, NdtManualLineUnit, NdtTicket, NdtTicketLine } from '../../types/ndt-tickets';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useOpenRecord } from '../downhole/recordPage';
import { NDT_DATE_INPUT, NDT_INPUT, NDT_PRIMARY, NDT_SECONDARY, NdtBadge, NdtField, NdtFormError, NdtPanel, ndtDay, type NdtColumn } from './NdtBits';

/** A ticket's date: a day ticket's work day, else the tenant day it was created. */
export function ndtTicketDate(t: Pick<NdtTicket, 'workDate' | 'createdAt' | 'scope'>, tz: string | null | undefined): { text: string; sub: string } {
  if (t.workDate) return { text: ndtDay(t.workDate), sub: 'Work day' };
  return { text: formatInstantTenantDay(t.createdAt, tz) ?? '—', sub: t.scope === 'final' ? 'Final ticket' : 'Whole job' };
}

export function ndtTicketColumns(tz: string | null | undefined, showJob = true): NdtColumn<NdtTicket>[] {
  const cols: NdtColumn<NdtTicket>[] = [
    { key: 'number', header: 'Ticket #', render: (t) => <span className="font-mono font-extrabold text-white">{t.ticketNumber || '—'}</span> },
  ];
  if (showJob) {
    cols.push({ key: 'job', header: 'Job', render: (t) => <span className="font-mono">{t.jobNumber || '—'}</span> });
    cols.push({
      key: 'customer',
      header: 'Customer / location',
      render: (t) => (
        <>
          <p className="font-semibold text-white">{t.customerName || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{[t.locationName, t.lsd].filter(Boolean).join(' · ') || '—'}</p>
        </>
      ),
    });
  }
  cols.push(
    { key: 'scope', header: 'Covers', render: (t) => ndtTicketScopeText(t.scope, t.workDate) },
    {
      key: 'date',
      header: 'Date',
      render: (t) => {
        const d = ndtTicketDate(t, tz);
        return (
          <>
            <p>{d.text}</p>
            <p className="mt-0.5 text-xs text-fieldiq-muted">{d.sub}</p>
          </>
        );
      },
    },
    { key: 'subtotal', header: 'Subtotal', align: 'right', render: (t) => <span data-testid="ndt-ticket-row-subtotal">{ndtSubtotalText(t.subtotal, t.lineCount, t.currency)}</span> },
    { key: 'status', header: 'Status', render: (t) => <NdtBadge skin={ndtTicketStatusSkin(t.status)} value={t.status} /> },
  );
  return cols;
}

export function NdtTicketCard({ t, tz }: { t: NdtTicket; tz: string | null | undefined }) {
  const d = ndtTicketDate(t, tz);
  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-sm font-extrabold text-white">{t.ticketNumber || '—'}</p>
          <p className="mt-0.5 truncate text-xs text-fieldiq-muted">
            {t.jobNumber || '—'} · {t.customerName || '—'}
          </p>
        </div>
        <NdtBadge skin={ndtTicketStatusSkin(t.status)} value={t.status} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fieldiq-text">
        <span>
          {d.sub} {d.text}
        </span>
        <span className="font-mono">{ndtSubtotalText(t.subtotal, t.lineCount, t.currency)}</span>
      </div>
    </div>
  );
}

// ─── The job's Field tickets panel ───────────────────────────────────────────

/**
 * On /ndt-inspection/jobs/:id. Its tickets (GET /jobs/:id/tickets), "Close day"
 * for a chosen work date on an active or complete per-day job (NDT_WORK), and
 * "Rebuild tickets" on a complete job (NDT_DISPATCH) — the replacement after a
 * void. The API refuses and says why; nothing here decides a day is ticketed.
 */
export function NdtJobTicketsPanel({ job, today, canWork, canDispatch }: { job: NdtJobDetail; today: string | null; canWork: boolean; canDispatch: boolean }) {
  const tickets = useNdtJobTickets(job.id);
  const closeDay = useCloseNdtDay();
  const rebuild = useRebuildNdtTickets();
  const openRecord = useOpenRecord();
  const [workDate, setWorkDate] = useState(today ?? '');
  const [error, setError] = useState<string | null>(null);
  const rows = tickets.data?.data ?? [];
  const tz = tickets.data?.meta.timeZone ?? null;
  const failure = queryLoadFailure(tickets);
  const perDay = job.ticketMode === 'per_day';
  const canClose = canWork && perDay && (job.status === 'active' || job.status === 'complete');

  const onClose = () => {
    setError(null);
    if (!workDate) return setError('Choose the work date to close out.');
    closeDay.mutate(
      { jobId: job.id, workDate },
      {
        onSuccess: (res) => {
          toast.success(res.data?.ticketNumber ? `${ndtDay(workDate)} is on ${res.data.ticketNumber}` : 'Day closed out');
          if (res.data?.id) openRecord(`/ndt-inspection/tickets/${res.data.id}`);
        },
        onError: (err) => setError(ndtTicketRefusal(err, 'The day was not closed out.').message),
      },
    );
  };

  const onRebuild = () => {
    setError(null);
    rebuild.mutate(job.id, {
      onSuccess: (res) => toast.success(`${res.data.length} ticket${res.data.length === 1 ? '' : 's'} drafted or updated`),
      onError: (err) => setError(ndtTicketRefusal(err, 'No ticket was rebuilt.').message),
    });
  };

  return (
    <NdtPanel
      eyebrow="Billing"
      title="Field tickets"
      testId="ndt-job-tickets"
      actions={
        canDispatch && job.status === 'complete' ? (
          <button type="button" className={NDT_SECONDARY} onClick={onRebuild} disabled={rebuild.isPending} data-testid="ndt-job-rebuild-tickets">
            {rebuild.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="size-4" aria-hidden="true" />}
            Rebuild tickets
          </button>
        ) : undefined
      }
    >
      <div className="space-y-4 px-4 py-4">
        <p className="text-xs text-fieldiq-muted">
          {perDay
            ? 'One ticket per work day. Completing the job sweeps anything left onto the day tickets or a final ticket.'
            : job.ticketMode === 'per_job'
              ? 'One ticket for the whole job, drafted when the job is completed.'
              : ''}
        </p>
        {canClose && (
          <div className="flex flex-wrap items-end gap-2" data-testid="ndt-job-close-day">
            <NdtField label="Work date">
              <input type="date" className={`${NDT_DATE_INPUT} w-44`} value={workDate} max={today ?? undefined} onChange={(e) => setWorkDate(e.target.value)} data-testid="ndt-job-close-day-date" />
            </NdtField>
            <button type="button" className={NDT_SECONDARY} onClick={onClose} disabled={closeDay.isPending} data-testid="ndt-job-close-day-submit">
              {closeDay.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CalendarCheck className="size-4" aria-hidden="true" />}
              Close day
            </button>
          </div>
        )}
        <NdtFormError message={error} />
        {tickets.isLoading ? (
          <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading the job's tickets" />
        ) : failure ? (
          <LoadFailureNotice error={queryFailureError(tickets)} kind={failure} subject="this job's tickets" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-fieldiq-text" data-testid="ndt-job-tickets-none">
            {job.status === 'cancelled'
              ? 'This job was cancelled and has no field tickets.'
              : perDay
                ? 'No days are closed out yet.'
                : job.status === 'complete'
                  ? 'This job has no field ticket.'
                  : 'The field ticket is drafted when this job is completed.'}
          </p>
        ) : (
          <ul className="divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
            {rows.map((t) => {
              const d = ndtTicketDate(t, tz);
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => openRecord(`/ndt-inspection/tickets/${t.id}`)}
                    data-testid="ndt-job-ticket"
                    className={`flex w-full flex-wrap items-center gap-3 border-l-4 px-3 py-2.5 text-left outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${ndtTicketStatusSkin(t.status).rail}`}
                  >
                    <span className="font-mono text-sm font-extrabold text-white">{t.ticketNumber}</span>
                    <NdtBadge skin={ndtTicketStatusSkin(t.status)} value={t.status} />
                    <span className="text-sm text-fieldiq-text">
                      {d.sub} {d.text}
                    </span>
                    <span className="ml-auto font-mono text-sm tabular-nums text-white">{ndtSubtotalText(t.subtotal, t.lineCount, t.currency)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </NdtPanel>
  );
}

// ─── Line forms ──────────────────────────────────────────────────────────────

const big = (on: boolean) => (on ? 'h-12 text-base' : '');
const formRow = (on: boolean) => (on ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2');
const bigBtn = (cls: string, on: boolean) => (on ? `${cls} h-12 justify-center` : cls);

type AddMode = 'card' | 'office';

/**
 * Add a line. From the rate card: a rate line (rate_id) and a quantity — the
 * office may also set its own price on it. The office alone may add a line the
 * rate card does not hold: unit, description, rate and quantity.
 */
export function NdtAddLineForm({
  ticketId,
  customerId,
  currency,
  day,
  canPrice,
  bigControls = false,
  submit,
  onDone,
  onCancel,
}: {
  ticketId: string;
  customerId: string | null;
  currency: string | null;
  /** The day the rate card is read for: the ticket's work day, else the tenant's today. */
  day: string | null;
  canPrice: boolean;
  bigControls?: boolean;
  /** The phone sends through the field route; omitted, the office route. */
  submit?: (args: { ticketId: string; body: NdtLineCreate }, cb: { onSuccess: () => void; onError: (e: unknown) => void }) => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const add = useAddNdtTicketLine();
  const rates = useNdtRates(day);
  const [mode, setMode] = useState<AddMode>('card');
  const [rateId, setRateId] = useState('');
  const [unit, setUnit] = useState<NdtManualLineUnit>('per_hour');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const choices = useMemo(() => ndtManualRateChoices(rates.data?.data ?? [], customerId), [rates.data, customerId]);
  const chosen = choices.find((r) => r.id === rateId) ?? null;
  const input = `${NDT_INPUT} ${big(bigControls)}`;
  const shownBasis = mode === 'card' ? (chosen ? ndtUnitBasis(chosen.unit, chosen.distanceUnit) : '') : ndtUnitBasis(unit, null);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const q = ndtAmountFromInput(quantity);
    if (q === null || Number.isNaN(q)) return setError('Enter the quantity as a number of 0 or more, with at most 2 decimals.');
    const body: NdtLineCreate = { quantity: q };
    if (mode === 'card') {
      if (!rateId) return setError('Choose a line from the rate card.');
      body.rate_id = rateId;
      if (description.trim()) body.description = description.trim();
      if (canPrice && price.trim()) {
        const p = ndtAmountFromInput(price);
        if (p === null || Number.isNaN(p)) return setError('Enter the price as a number of 0 or more, with at most 2 decimals.');
        body.rate = p;
      }
    } else {
      if (!description.trim()) return setError('A line not on the rate card needs a description.');
      const p = ndtAmountFromInput(price);
      if (p === null || Number.isNaN(p)) return setError('Enter the price as a number of 0 or more, with at most 2 decimals.');
      body.unit = unit;
      body.description = description.trim();
      body.rate = p;
    }
    const cb = {
      onSuccess: () => {
        setPending(false);
        toast.success('Line added');
        onDone();
      },
      onError: (err: unknown) => {
        setPending(false);
        setError(ndtTicketRefusal(err, 'The line was not added.').message);
      },
    };
    setPending(true);
    if (submit) submit({ ticketId, body }, cb);
    else add.mutate({ ticketId, body }, cb);
  };

  const rateFailure = queryLoadFailure(rates);

  return (
    <form onSubmit={onSubmit} className="space-y-3" noValidate data-testid="ndt-add-line-form">
      <NdtFormError message={error} />
      {canPrice && (
        <fieldset className="flex flex-wrap gap-4">
          <legend className="sr-only">Where the price comes from</legend>
          <label className="flex items-center gap-2 text-sm text-fieldiq-text">
            <input type="radio" checked={mode === 'card'} onChange={() => setMode('card')} className="size-4 accent-fieldiq-accent" data-testid="ndt-line-mode-card" />
            From the rate card
          </label>
          <label className="flex items-center gap-2 text-sm text-fieldiq-text">
            <input type="radio" checked={mode === 'office'} onChange={() => setMode('office')} className="size-4 accent-fieldiq-accent" data-testid="ndt-line-mode-office" />
            Not on the rate card
          </label>
        </fieldset>
      )}

      {mode === 'card' ? (
        <NdtField
          label="Rate card line"
          hint={
            rateFailure
              ? 'The rate card could not be loaded.'
              : rates.isSuccess && choices.length === 0
                ? 'No hours, mileage, consumable, flat or crew-day lines are on the rate card for this customer on this day. The office adds them in Settings → NDT.'
                : undefined
          }
        >
          <select className={input} value={rateId} onChange={(e) => setRateId(e.target.value)} data-testid="ndt-line-rate">
            <option value="">{rates.isLoading ? 'Loading…' : 'Choose a line'}</option>
            {choices.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code} · {r.name} — {ndtRateText(r.rate, r.currency, r.unit, r.distanceUnit)}
                {r.customerId ? '' : ' (default)'}
              </option>
            ))}
          </select>
        </NdtField>
      ) : (
        <>
          <NdtField label="Unit">
            <select className={input} value={unit} onChange={(e) => setUnit(e.target.value as NdtManualLineUnit)} data-testid="ndt-line-unit">
              {NDT_OFFICE_LINE_UNITS.map((u) => (
                <option key={u} value={u}>
                  {ndtLineUnitLabel(u)}
                </option>
              ))}
            </select>
          </NdtField>
        </>
      )}

      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
        <NdtField label="Quantity">
          <input className={input} inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="0" data-testid="ndt-line-quantity" />
        </NdtField>
        <span className="pb-2.5 text-sm font-semibold text-fieldiq-text" data-testid="ndt-line-basis">
          {shownBasis || '—'}
        </span>
      </div>

      <NdtField label={mode === 'office' ? 'Description' : 'Description (optional)'}>
        <input className={input} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} data-testid="ndt-line-description" />
      </NdtField>

      {canPrice && (
        <NdtField label={mode === 'office' ? `Price (${currency ?? '—'})` : `Office price (${currency ?? '—'}, optional)`} hint={mode === 'card' ? 'Leave blank to use the rate card. 0 is no charge.' : '0 is no charge.'}>
          <input className={input} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" data-testid="ndt-line-price" />
        </NdtField>
      )}

      <div className={formRow(bigControls)}>
        <button type="button" className={bigBtn(NDT_SECONDARY, bigControls)} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={bigBtn(NDT_PRIMARY, bigControls)} disabled={pending} data-testid="ndt-line-add-submit">
          {pending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Add line
        </button>
      </div>
    </form>
  );
}

type PriceMode = 'keep' | 'set' | 'card';

/**
 * Edit a line: its quantity (anyone on the ticket), its description (a manual
 * line), and — the office — its price: set one (0 is no charge) or put it back
 * on the rate card. Only what changed is sent.
 */
export function NdtEditLineForm({
  line,
  currency,
  canPrice,
  bigControls = false,
  onDone,
  onCancel,
}: {
  line: Pick<NdtTicketLine, 'id' | 'quantity' | 'description' | 'source' | 'rate' | 'rateSource' | 'unit' | 'distanceUnit'>;
  currency: string | null;
  canPrice: boolean;
  bigControls?: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const update = useUpdateNdtTicketLine();
  const [quantity, setQuantity] = useState(line.quantity === null ? '' : String(line.quantity));
  const [description, setDescription] = useState(line.description);
  const [priceMode, setPriceMode] = useState<PriceMode>('keep');
  const [price, setPrice] = useState(line.rateSource === 'office' && line.rate !== null ? String(line.rate) : '');
  const [error, setError] = useState<string | null>(null);
  const input = `${NDT_INPUT} ${big(bigControls)}`;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const body: NdtLineUpdate = {};
    const q = ndtAmountFromInput(quantity);
    if (q === null || Number.isNaN(q)) return setError('Enter the quantity as a number of 0 or more, with at most 2 decimals.');
    if (q !== line.quantity) body.quantity = q;
    if (line.source !== 'auto' && description.trim() !== line.description) {
      if (!description.trim()) return setError('The description cannot be empty.');
      body.description = description.trim();
    }
    if (canPrice && priceMode === 'set') {
      const p = ndtAmountFromInput(price);
      if (p === null || Number.isNaN(p)) return setError('Enter the price as a number of 0 or more, with at most 2 decimals.');
      body.rate = p;
    }
    if (canPrice && priceMode === 'card') body.rate = null;
    if (Object.keys(body).length === 0) return onCancel();
    update.mutate(
      { lineId: line.id, body },
      {
        onSuccess: () => {
          toast.success('Line updated');
          onDone();
        },
        onError: (err) => setError(ndtTicketRefusal(err, 'The line was not changed.').message),
      },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3" noValidate data-testid="ndt-edit-line-form">
      <NdtFormError message={error} />
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
        <NdtField label="Quantity">
          <input className={input} inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="ndt-edit-line-quantity" autoFocus />
        </NdtField>
        <span className="pb-2.5 text-sm font-semibold text-fieldiq-text">{ndtUnitBasis(line.unit, line.distanceUnit) || '—'}</span>
      </div>
      {line.source === 'auto' ? (
        <p className="text-xs text-fieldiq-muted">An automatic line keeps its description.</p>
      ) : (
        <NdtField label="Description">
          <input className={input} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} />
        </NdtField>
      )}
      {canPrice && (
        <fieldset className="space-y-2" data-testid="ndt-edit-line-price">
          <legend className="mb-1 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            Price — now {ndtRateText(line.rate, currency, line.unit, line.distanceUnit)}
            {line.rateSource === 'office' ? ' (set by the office)' : line.rateSource === 'rate_card' ? ' (rate card)' : ''}
          </legend>
          {(['keep', 'set', 'card'] as const).map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm text-fieldiq-text">
              <input type="radio" checked={priceMode === m} onChange={() => setPriceMode(m)} className="size-4 accent-fieldiq-accent" data-testid={`ndt-edit-line-price-${m}`} />
              {m === 'keep' ? 'Leave the price as it is' : m === 'set' ? 'Set the price (0 is no charge)' : 'Back to the rate card'}
            </label>
          ))}
          {priceMode === 'set' && (
            <input className={input} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" aria-label="Price" data-testid="ndt-edit-line-price-value" />
          )}
        </fieldset>
      )}
      <div className={formRow(bigControls)}>
        <button type="button" className={bigBtn(NDT_SECONDARY, bigControls)} onClick={onCancel} disabled={update.isPending}>
          Cancel
        </button>
        <button type="submit" className={bigBtn(NDT_PRIMARY, bigControls)} disabled={update.isPending} data-testid="ndt-edit-line-save">
          {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Save
        </button>
      </div>
    </form>
  );
}

/**
 * The office picks which rate line prices an unpriced line — a tie the API
 * would not guess (it names both codes), or a line priced in another currency.
 * Offers the same-unit lines in force on the line's day.
 */
export function NdtPickRateForm({
  line,
  customerId,
  day,
  onDone,
  onCancel,
}: {
  line: Pick<NdtTicketLine, 'id' | 'unit' | 'unpricedReason' | 'description'>;
  customerId: string | null;
  day: string | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const update = useUpdateNdtTicketLine();
  const rates = useNdtRates(day);
  const [rateId, setRateId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const choices = useMemo(() => ndtRatePickChoices(rates.data?.data ?? [], line.unit, customerId), [rates.data, line.unit, customerId]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!rateId) return setError('Choose the rate line.');
    update.mutate(
      { lineId: line.id, body: { rate_id: rateId } },
      {
        onSuccess: () => {
          toast.success('Rate chosen');
          onDone();
        },
        onError: (err) => setError(ndtTicketRefusal(err, 'The rate was not set.').message),
      },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3" noValidate data-testid="ndt-pick-rate-form">
      {line.unpricedReason && <p className="text-sm text-fieldiq-text">{line.unpricedReason}</p>}
      <NdtFormError message={error} />
      <NdtField
        label="Rate line"
        hint={queryLoadFailure(rates) ? 'The rate card could not be loaded.' : rates.isSuccess && choices.length === 0 ? `No ${ndtLineUnitLabel(line.unit).toLowerCase()} lines are on the rate card for this day.` : undefined}
      >
        <select className={NDT_INPUT} value={rateId} onChange={(e) => setRateId(e.target.value)} data-testid="ndt-pick-rate-select">
          <option value="">{rates.isLoading ? 'Loading…' : 'Choose a rate line'}</option>
          {choices.map((r) => (
            <option key={r.id} value={r.id}>
              {r.code} · {r.name} — {ndtRateText(r.rate, r.currency, r.unit, r.distanceUnit)}
              {r.customerId ? '' : ' (default)'}
            </option>
          ))}
        </select>
      </NdtField>
      <div className="flex justify-end gap-2">
        <button type="button" className={NDT_SECONDARY} onClick={onCancel} disabled={update.isPending}>
          Cancel
        </button>
        <button type="submit" className={NDT_PRIMARY} disabled={update.isPending} data-testid="ndt-pick-rate-save">
          {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Use this rate
        </button>
      </div>
    </form>
  );
}

/** Remove a line. An automatic line needs a reason (the API refuses without one); a removed exam line is a deliberate no-charge. */
export function NdtRemoveLineForm({
  line,
  bigControls = false,
  onDone,
  onCancel,
}: {
  line: Pick<NdtTicketLine, 'id' | 'description' | 'source' | 'examId'>;
  bigControls?: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const remove = useRemoveNdtTicketLine();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const needsReason = line.source === 'auto';

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (needsReason && !reason.trim()) return setError('A reason is required to remove an automatic line.');
    remove.mutate(
      { lineId: line.id, reason: reason.trim() || null },
      {
        onSuccess: () => {
          toast.success('Line removed');
          onDone();
        },
        onError: (err) => setError(ndtTicketRefusal(err, 'The line was not removed.').message),
      },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3" noValidate data-testid="ndt-remove-line-form">
      <p className="text-sm text-fieldiq-text">
        Remove <span className="font-bold">{line.description}</span>?{' '}
        {line.examId ? 'A removed exam line is not billed on a later ticket either.' : ''}
      </p>
      <NdtFormError message={error} />
      <NdtField label={needsReason ? 'Reason' : 'Reason (optional)'}>
        <textarea className={`${NDT_INPUT} h-20 py-2 ${bigControls ? 'text-base' : ''}`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="ndt-remove-line-reason" />
      </NdtField>
      <div className={formRow(bigControls)}>
        <button type="button" className={bigBtn(NDT_SECONDARY, bigControls)} onClick={onCancel} disabled={remove.isPending}>
          Keep it
        </button>
        <button
          type="submit"
          disabled={remove.isPending}
          data-testid="ndt-remove-line-confirm"
          className={`inline-flex items-center justify-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:cursor-not-allowed disabled:opacity-60 ${bigControls ? 'h-12' : 'h-9'}`}
        >
          {remove.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Remove line
        </button>
      </div>
    </form>
  );
}
